import { Link } from 'react-router-dom';

import { Alert, KpiCard } from '@/components/ui';
import { useKpisToday } from '@/lib/home-today-store';

/**
 * KpisRealsWidget — os KPIs do dia na home clássica (R8 Bloco E).
 *
 * FASE-C (redesign): os cards viraram o `KpiCard` canônico do design system —
 * altura igual na fileira, label 12px caps, valor em SF Mono tabular (o Oswald
 * itálico saiu dos números) e linha de contexto explicando cada número (o
 * diagnóstico apontou "R$ 0" solto, sem contexto).
 *
 * ── 07/08/2026: a fileira deixou de ser fixa em 4
 *
 * Eram sempre os mesmos quatro cards, para todo mundo. O Gabriel, do Compras,
 * abria a home e via "Em negociação: 5" — propostas do Comercial, que não são
 * trabalho dele. Agora o SERVIDOR decide quais KPIs são desta pessoa
 * (`/api/v1/kpis/today` escopa por departamento) e manda `null` no resto.
 *
 * Card que veio `null` não vira zero: some. Zero afirma "existem zero", e essa
 * é uma afirmação que só quem tem o módulo pode fazer.
 */

export function KpisRealsWidget() {
  const { data, isLoading, isError, error } = useKpisToday();

  if (isError) {
    return (
      <Alert variant="danger" rail>
        Não consegui carregar os KPIs do dia.
        <br />
        <span className="text-xs opacity-70">
          {error instanceof Error ? error.message : 'Erro desconhecido'}
        </span>
      </Alert>
    );
  }

  const foraDoPrazo = data?.chamados_fora_do_prazo?.value ?? 0;

  // Enquanto carrega, ainda não se sabe QUAIS cards são desta pessoa — mostrar
  // a fileira de quatro em "…" e depois encolher para um seria pior que
  // esperar. Um aviso curto, e a tela não pula.
  if (isLoading) {
    return (
      <section data-testid="kpis-real-widget">
        <h2 className="eyebrow mb-3">Resumo do dia</h2>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando os números…</p>
      </section>
    );
  }

  // ⛔ 13/10/2026 (D2): o cartão "A faturar" saiu (lia uma tabela que deixou de ser o funil). São três cartões no máximo.
  const visiveis = [data?.em_negociacao, data?.chamados_fora_do_prazo, data?.preventivas_no_mes].filter(Boolean).length;
  const temAlgum = visiveis > 0;

  // Quem não tem nenhum dos módulos não vê fileira nenhuma. A frase diz o
  // motivo em vez de deixar um buraco — e não promete tela que não existe.
  if (!temAlgum) {
    return (
      <section data-testid="kpis-real-widget">
        <h2 className="eyebrow mb-3">Resumo do dia</h2>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3" data-testid="kpis-sem-modulo">
          Os números do dia são por área — Comercial e Manutenção. Seu acesso não inclui nenhuma
          delas, então não há resumo para mostrar aqui.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="kpis-real-widget">
      <h2 className="eyebrow mb-3">Resumo do dia</h2>
      {/* A grade acompanha quantos cartões a pessoa vê: com três, três colunas a partir de `sm` (e um embaixo do
          outro no celular). ❌ Grade fixa de quatro deixaria o buraco onde o "A faturar" estava. */}
      <div className={`grid gap-3 ${GRADE[visiveis] ?? GRADE[3]}`} data-testid="kpis-grade" data-cartoes={visiveis}>
        {data?.em_negociacao && (
          <KpiCard
            data-testid="kpi-em-negociacao"
            label="Em negociação"
            value={formatNumber(data.em_negociacao.value)}
            context="propostas abertas no funil"
          />
        )}
        {data?.chamados_fora_do_prazo && (
          /* 🔴 A4 (06/10/2026): era "OS atrasadas", lendo uma tabela vazia — mostrava 0 sempre.
             Agora são os chamados ainda abertos com o prazo do contrato vencido, a mesma conta da
             aba Chamados dos Indicadores. Com algum, o cartão leva à lista; com zero, não é destino
             (régua da casa: zero não é um lugar para ir). */
          (() => {
            const cartao = (
              <KpiCard
                data-testid="kpi-chamados-fora-do-prazo"
                label={data.chamados_fora_do_prazo.label}
                value={
                  foraDoPrazo > 0 ? (
                    <span className="text-red dark:text-red-glow">{formatNumber(foraDoPrazo)}</span>
                  ) : (
                    formatNumber(foraDoPrazo)
                  )
                }
                context={foraDoPrazo > 0 ? 'chamados ainda abertos, com o prazo vencido · abrir a lista' : 'nenhum chamado aberto com prazo vencido'}
              />
            );
            return foraDoPrazo > 0 ? (
              <Link
                to="/menu/manutencao/indicadores?aba=chamados&lista=abertos-fora-do-prazo"
                className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red"
                aria-label="Chamados abertos fora do prazo: abrir a lista"
                data-testid="kpi-chamados-fora-do-prazo-link"
              >
                {cartao}
              </Link>
            ) : (
              cartao
            );
          })()
        )}
        {data?.preventivas_no_mes && (
          /* 🔴 24/09/2026: de "Preventivas no mês: 22" para "Clientes visitados
             no mês: 10, de 24 com visita prometida".
             ⚠️ O rótulo e o contexto vêm do SERVIDOR agora — o texto fixo que
             estava aqui dizia "ordens de serviço do mês corrente", e não eram
             ordens de serviço: são tarefas do Auvo. Texto de tela escrito à mão
             ao lado de um número que vem de fora envelhece sozinho. */
          <KpiCard
            data-testid="kpi-preventivas"
            label={data.preventivas_no_mes.label}
            value={formatNumber(data.preventivas_no_mes.value)}
            context={data.preventivas_no_mes.hint ?? 'no mês corrente'}
          />
        )}
      </div>
    </section>
  );
}

/**
 * As colunas da grade por quantidade de cartões visíveis.
 * ⚠️ Três lado a lado só a partir de `xl`: medido em 13/10 (texto-inteiro), com `sm:grid-cols-3` os rótulos "Em
 * negociação" e "Fora do prazo" quebravam em duas linhas em 768 px. Abaixo de `xl`, um embaixo do outro: sem buraco na
 * grade (o que o D2 pede) e sem rótulo partido.
 */
const GRADE: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-1 xl:grid-cols-3',
};

function formatNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}
